import type {
    GameConfig,
    GameObject,
    ObjectLevel,
    Production,
    Quest,
    QuestCondition,
    QuestReward,
    Resource,
    ResourceAmount,
    Upgrade,
} from '../types.js';

/** Normalizes wizard JSON; player levels are 1-based, arrays are 0-based. */

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function fail(path: string, message: string): never {
    throw new Error(`Invalid game config at ${path}: ${message}`);
}

function requireString(value: unknown, path: string): void {
    if (typeof value !== 'string' || !value.trim()) fail(path, 'expected a non-empty string');
}

function requireNonNegativeNumber(value: unknown, path: string): void {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        fail(path, 'expected a non-negative finite number');
    }
}

function requirePositiveNumber(value: unknown, path: string): void {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        fail(path, 'expected a positive finite number');
    }
}

function requirePositiveInteger(value: unknown, path: string): void {
    if (!Number.isInteger(value) || (value as number) <= 0) {
        fail(path, 'expected a positive integer');
    }
}

function validateResourceAmount(value: unknown, path: string): void {
    if (!isRecord(value)) fail(path, 'expected an object');
    requireString(value.resource, `${path}.resource`);
    requireNonNegativeNumber(value.quantity, `${path}.quantity`);
}

function validateProduction(value: unknown, path: string, resources: Set<string> | null = null): void {
    if (!isRecord(value)) fail(path, 'expected an object');
    requireString(value.resource, `${path}.resource`);
    if (resources && !resources.has(value.resource as string)) {
        fail(`${path}.resource`, 'references an unknown resource');
    }
    requirePositiveNumber(value.quantity, `${path}.quantity`);
    requirePositiveNumber(value.timeMs, `${path}.timeMs`);
    if (value.capacity !== undefined && value.capacity !== null) {
        requireNonNegativeNumber(value.capacity, `${path}.capacity`);
    }
    if (!Array.isArray(value.inputs)) fail(`${path}.inputs`, 'expected an array');
    value.inputs.forEach((input, index) => {
        const inputPath = `${path}.inputs[${index}]`;
        if (!isRecord(input)) fail(inputPath, 'expected an object');
        requireString(input.resource, `${inputPath}.resource`);
        if (resources && !resources.has(input.resource as string)) {
            fail(`${inputPath}.resource`, 'references an unknown resource');
        }
        requirePositiveNumber(input.quantity, `${inputPath}.quantity`);
    });
}

function validateWorld(world: unknown): { width: number; height: number } {
    if (world === undefined) return { width: 16, height: 16 };
    if (!isRecord(world)) fail('world', 'expected an object');

    if (world.gridWidth !== undefined) requirePositiveInteger(world.gridWidth, 'world.gridWidth');
    if (world.gridHeight !== undefined) requirePositiveInteger(world.gridHeight, 'world.gridHeight');
    if (world.cellSize !== undefined) requirePositiveNumber(world.cellSize, 'world.cellSize');
    if (world.tileWidth !== undefined) requirePositiveNumber(world.tileWidth, 'world.tileWidth');
    if (world.tileHeight !== undefined) requirePositiveNumber(world.tileHeight, 'world.tileHeight');
    if (world.foreground !== undefined && world.foreground !== null) {
        requireString(world.foreground, 'world.foreground');
    }
    if (world.foregroundOpacity !== undefined) {
        if (
            typeof world.foregroundOpacity !== 'number'
            || !Number.isFinite(world.foregroundOpacity)
            || world.foregroundOpacity < 0
            || world.foregroundOpacity > 1
        ) {
            fail('world.foregroundOpacity', 'expected a number between 0 and 1');
        }
    }

    if (isRecord(world.ground) && world.ground.gridColor !== undefined && world.ground.gridColor !== null) {
        requireString(world.ground.gridColor, 'world.ground.gridColor');
    }

    return {
        width: (world.gridWidth as number | undefined) ?? 16,
        height: (world.gridHeight as number | undefined) ?? 16,
    };
}

function validateConfigResources(resources: unknown): Set<string> | null {
    if (resources === undefined) return null;
    if (!Array.isArray(resources)) fail('resources', 'expected an array');

    const ids = new Set<string>();
    resources.forEach((value, index) => {
        const path = `resources[${index}]`;
        if (!isRecord(value)) fail(path, 'expected an object');
        requireString(value.id, `${path}.id`);
        if (ids.has(value.id as string)) fail(`${path}.id`, 'must be unique');
        ids.add(value.id as string);
        if (value.type !== 'raw' && value.type !== 'processed') fail(`${path}.type`, 'is invalid');
        if (value.initial !== undefined) requireNonNegativeNumber(value.initial, `${path}.initial`);
    });
    return ids;
}

function validateConfigObjects(
    objects: unknown,
    resources: Set<string> | null = null,
    categories: Set<string> | null = null,
): Map<string, { w: number; h: number }> {
    if (objects === undefined) return new Map();
    if (!Array.isArray(objects)) fail('objects', 'expected an array');

    const footprints = new Map<string, { w: number; h: number }>();
    objects.forEach((value, index) => {
        const path = `objects[${index}]`;
        if (!isRecord(value)) fail(path, 'expected an object');
        requireString(value.id, `${path}.id`);
        const id = value.id as string;
        if (footprints.has(id)) fail(`${path}.id`, 'must be unique');

        if (!isRecord(value.footprint)) fail(`${path}.footprint`, 'expected an object');
        requirePositiveInteger(value.footprint.w, `${path}.footprint.w`);
        requirePositiveInteger(value.footprint.h, `${path}.footprint.h`);
        if (value.category !== undefined) validateReference(value.category, `${path}.category`, categories);
        if (!Array.isArray(value.levels)) fail(`${path}.levels`, 'expected an array');

        value.levels.forEach((level, levelIndex) => {
            const levelPath = `${path}.levels[${levelIndex}]`;
            if (!isRecord(level)) fail(levelPath, 'expected an object');
            if (!Array.isArray(level.construction)) fail(`${levelPath}.construction`, 'expected an array');
            level.construction.forEach((cost, costIndex) => {
                validateResourceAmount(cost, `${levelPath}.construction[${costIndex}]`);
                const amount = cost as JsonRecord;
                if (resources && !resources.has(amount.resource as string)) {
                    fail(`${levelPath}.construction[${costIndex}].resource`, 'references an unknown resource');
                }
            });
            if (level.buildTimeMs !== undefined) {
                requireNonNegativeNumber(level.buildTimeMs, `${levelPath}.buildTimeMs`);
            }
            if (level.production != null) {
                const lines = Array.isArray(level.production) ? level.production : [level.production];
                lines.forEach((line, lineIndex) => {
                    validateProduction(line, `${levelPath}.production[${lineIndex}]`, resources);
                });
            }
        });

        footprints.set(id, {
            w: value.footprint.w as number,
            h: value.footprint.h as number,
        });
    });
    return footprints;
}

function validateCategories(categories: unknown): Set<string> | null {
    if (categories === undefined) return null;
    if (!Array.isArray(categories)) fail('categories', 'expected an array');

    const ids = new Set<string>();
    categories.forEach((value, index) => {
        const path = `categories[${index}]`;
        if (!isRecord(value)) fail(path, 'expected an object');
        requireString(value.id, `${path}.id`);
        if (ids.has(value.id as string)) fail(`${path}.id`, 'must be unique');
        ids.add(value.id as string);
        if (value.slots !== undefined) {
            if (!Number.isInteger(value.slots) || (value.slots as number) < 0) {
                fail(`${path}.slots`, 'expected a non-negative integer');
            }
        }
    });
    return ids;
}

function validateInitialPlacements(
    placements: unknown,
    width: number,
    height: number,
    footprints: Map<string, { w: number; h: number }>,
    resources: Set<string> | null,
): void {
    if (placements === undefined) return;
    if (!Array.isArray(placements)) fail('initialPlacements', 'expected an array');

    placements.forEach((value, index) => {
        const path = `initialPlacements[${index}]`;
        if (!isRecord(value)) fail(path, 'expected an object');
        requireString(value.object, `${path}.object`);
        if (!footprints.has(value.object as string)) fail(`${path}.object`, 'references an unknown object');
        if (!Number.isInteger(value.col) || !Number.isInteger(value.row)) {
            fail(path, 'col and row must be integers');
        }

        const footprint = footprints.get(value.object as string)!;
        if (
            (value.col as number) < 0
            || (value.row as number) < 0
            || (value.col as number) + footprint.w > width
            || (value.row as number) + footprint.h > height
        ) {
            fail(path, 'is outside the world bounds');
        }

        if (value.state !== undefined) validateInitialState(value.state, `${path}.state`, resources);
    });
}

function validateInitialState(value: unknown, path: string, resources: Set<string> | null): void {
    if (!isRecord(value)) fail(path, 'expected an object');
    if (value.level !== undefined) requirePositiveInteger(value.level, `${path}.level`);
    if (value.constructionMs !== undefined && value.constructionMs !== null) {
        requireNonNegativeNumber(value.constructionMs, `${path}.constructionMs`);
    }
    if (value.constructionTotalMs !== undefined && value.constructionTotalMs !== null) {
        requireNonNegativeNumber(value.constructionTotalMs, `${path}.constructionTotalMs`);
    }

    const validateOutputValues = (output: unknown, outputPath: string, allowNull: boolean): void => {
        if (typeof output === 'number') {
            requireNonNegativeNumber(output, outputPath);
            return;
        }
        if (allowNull && output === null) return;
        if (!isRecord(output)) fail(outputPath, 'expected a number or resource map');
        Object.entries(output).forEach(([resource, amount]) => {
            if (resources && !resources.has(resource)) fail(`${outputPath}.${resource}`, 'references an unknown resource');
            if (allowNull && amount === null) return;
            requireNonNegativeNumber(amount, `${outputPath}.${resource}`);
        });
    };

    if (value.stored !== undefined) validateOutputValues(value.stored, `${path}.stored`, false);
    if (value.capacity !== undefined) validateOutputValues(value.capacity, `${path}.capacity`, true);
    if (value.remainingMs !== undefined) validateOutputValues(value.remainingMs, `${path}.remainingMs`, true);
}

function validateReference(value: unknown, path: string, known: Set<string> | null): void {
    requireString(value, path);
    if (known && !known.has(value as string)) fail(path, 'references an unknown id');
}

function validateProgressionConfig(
    value: unknown,
    resources: Set<string> | null,
    objects: Set<string>,
): void {
    if (value === undefined) return;
    if (!Array.isArray(value)) fail('milestones', 'expected an array');

    value.forEach((entry, index) => {
        const path = `milestones[${index}]`;
        if (!isRecord(entry)) fail(path, 'expected an object');
        requireString(entry.id, `${path}.id`);
        if (!isRecord(entry.trigger)) fail(`${path}.trigger`, 'expected an object');
        if (
            entry.trigger.type !== 'resource-collection'
            && entry.trigger.type !== 'area-expansion'
            && entry.trigger.type !== 'new-buildings-unlock'
        ) {
            fail(`${path}.trigger.type`, 'is invalid');
        }
        if (entry.trigger.resource !== undefined) {
            validateReference(entry.trigger.resource, `${path}.trigger.resource`, resources);
        }
        if (entry.trigger.object !== undefined) {
            validateReference(entry.trigger.object, `${path}.trigger.object`, objects);
        }
        if (entry.trigger.quantity !== undefined) {
            requireNonNegativeNumber(entry.trigger.quantity, `${path}.trigger.quantity`);
        }
        if (entry.trigger.level !== undefined) {
            requirePositiveInteger(entry.trigger.level, `${path}.trigger.level`);
        }
        if (!Array.isArray(entry.rewards) || !entry.rewards.every((reward) => typeof reward === 'string')) {
            fail(`${path}.rewards`, 'expected an array of strings');
        }
        if (!isRecord(entry.popup)) fail(`${path}.popup`, 'expected an object');
        if (entry.popup.title !== undefined) requireString(entry.popup.title, `${path}.popup.title`);
        if (entry.popup.message !== undefined) requireString(entry.popup.message, `${path}.popup.message`);
    });

    const ids = new Set<string>();
    value.forEach((entry, index) => {
        const id = (entry as JsonRecord).id as string;
        if (ids.has(id)) fail(`milestones[${index}].id`, 'must be unique');
        ids.add(id);
    });
}

function validateQuestConfig(
    value: unknown,
    resources: Set<string> | null,
    objects: Set<string>,
    categories: Set<string> | null,
): void {
    if (value === undefined) return;
    if (!Array.isArray(value)) fail('quests', 'expected an array');

    const ids = new Set<string>();
    value.forEach((entry, index) => {
        const path = `quests[${index}]`;
        if (!isRecord(entry)) fail(path, 'expected an object');
        requireString(entry.id, `${path}.id`);
        if (ids.has(entry.id as string)) fail(`${path}.id`, 'must be unique');
        ids.add(entry.id as string);
        requireString(entry.name, `${path}.name`);
        requireString(entry.message, `${path}.message`);

        if (entry.conditions !== undefined) {
            if (!Array.isArray(entry.conditions)) fail(`${path}.conditions`, 'expected an array');
            entry.conditions.forEach((condition, conditionIndex) => {
                const conditionPath = `${path}.conditions[${conditionIndex}]`;
                if (!isRecord(condition)) fail(conditionPath, 'expected an object');
                if (condition.type !== 'building' && condition.type !== 'resource') {
                    fail(`${conditionPath}.type`, 'is invalid');
                }
                if (condition.object !== undefined) {
                    validateReference(condition.object, `${conditionPath}.object`, objects);
                }
                if (condition.resource !== undefined) {
                    validateReference(condition.resource, `${conditionPath}.resource`, resources);
                }
                if (condition.quantity !== undefined) {
                    requirePositiveNumber(condition.quantity, `${conditionPath}.quantity`);
                }
            });
        }

        if (entry.goal !== undefined && entry.goal !== 'building') fail(`${path}.goal`, 'is invalid');
        if (entry.object !== undefined) validateReference(entry.object, `${path}.object`, objects);
        if (entry.reward !== undefined) {
            validateResourceAmount(entry.reward, `${path}.reward`);
            const reward = entry.reward as JsonRecord;
            validateReference(reward.resource, `${path}.reward.resource`, resources);
        }

        if (entry.rewards !== undefined) {
            if (!Array.isArray(entry.rewards)) fail(`${path}.rewards`, 'expected an array');
            entry.rewards.forEach((reward, rewardIndex) => {
                const rewardPath = `${path}.rewards[${rewardIndex}]`;
                if (!isRecord(reward)) fail(rewardPath, 'expected an object');
                if (reward.type === 'resource') {
                    validateReference(reward.resource, `${rewardPath}.resource`, resources);
                    if (reward.quantity !== undefined) {
                        requireNonNegativeNumber(reward.quantity, `${rewardPath}.quantity`);
                    }
                } else if (reward.type === 'unlock') {
                    validateReference(reward.object, `${rewardPath}.object`, objects);
                } else if (reward.type === 'slot') {
                    validateReference(reward.category, `${rewardPath}.category`, categories);
                    if (reward.quantity !== undefined) {
                        requirePositiveNumber(reward.quantity, `${rewardPath}.quantity`);
                    }
                } else {
                    fail(`${rewardPath}.type`, 'is invalid');
                }
            });
        }

        if (entry.speaker !== undefined) {
            const speakerPath = `${path}.speaker`;
            if (!isRecord(entry.speaker)) fail(speakerPath, 'expected an object');
            requireString(entry.speaker.name, `${speakerPath}.name`);
            if (entry.speaker.role !== undefined && entry.speaker.role !== null) {
                requireString(entry.speaker.role, `${speakerPath}.role`);
            }
            if (entry.speaker.portrait !== undefined && entry.speaker.portrait !== null) {
                requireString(entry.speaker.portrait, `${speakerPath}.portrait`);
            }
        }
    });
}

function validateShopConfig(value: unknown, resources: Set<string> | null): void {
    if (value === undefined) return;
    if (!isRecord(value)) fail('shop', 'expected an object');
    if (!Array.isArray(value.offers)) fail('shop.offers', 'expected an array');

    value.offers.forEach((offer, index) => {
        const path = `shop.offers[${index}]`;
        if (!isRecord(offer)) fail(path, 'expected an object');
        validateReference(offer.resource, `${path}.resource`, resources);
        requirePositiveNumber(offer.quantity, `${path}.quantity`);
        requirePositiveNumber(offer.price, `${path}.price`);
    });
}

function validateStringArray(value: unknown, path: string): void {
    if (value !== undefined && (!Array.isArray(value) || !value.every((entry) => typeof entry === 'string'))) {
        fail(path, 'expected an array of strings');
    }
}

/** Themes with a CSS file under css/themes/ and (optionally) img/<name>/ art. */
const KNOWN_THEMES = new Set(['wood', 'futuristic', 'neon']);

/**
 * Normalise `ui.interface` to a known theme slug (`neon` is a legacy alias
 * for `futuristic`). Unknown slugs pass through so the wizard can ship a
 * theme before its stylesheet exists; the wood art fallback keeps the HUD
 * usable, and a warning flags the gap.
 */
function normaliseInterface(raw: unknown): string | null {
    if (raw == null) return null;
    if (typeof raw !== 'string') return null;
    if (raw === 'neon') return 'futuristic';
    if (!KNOWN_THEMES.has(raw)) {
        console.warn(`Unknown ui.interface "${raw}" — no stylesheet under css/themes/, falling back to the default colours.`);
    }
    return raw;
}

/** Validate the authoring boundary before the runtime consumes JSON. */
export function parseGameConfig(value: unknown): GameConfig {
    if (!isRecord(value)) fail('root', 'expected an object');

    const { width, height } = validateWorld(value.world);
    const resources = validateConfigResources(value.resources);
    const categories = validateCategories(value.categories);
    const footprints = validateConfigObjects(value.objects, resources, categories);
    const objects = new Set(footprints.keys());
    validateInitialPlacements(value.initialPlacements, width, height, footprints, resources);
    validateProgressionConfig(value.milestones, resources, objects);
    validateQuestConfig(value.quests, resources, objects, categories);
    validateShopConfig(value.shop, resources);
    validateStringArray(value.challenges, 'challenges');
    validateStringArray(value.economics, 'economics');
    if (value.currency !== undefined) validateReference(value.currency, 'currency', resources);

    if (value.session !== undefined) {
        if (!isRecord(value.session)) fail('session', 'expected an object');
        if (value.session.timeLimitMs !== null && value.session.timeLimitMs !== undefined) {
            requirePositiveNumber(value.session.timeLimitMs, 'session.timeLimitMs');
        }
    }

    // Normalise ui.interface: rewrite legacy aliases, leave unknown values.
    if (isRecord(value.ui)) {
        const raw = value.ui.interface;
        value.ui.interface = normaliseInterface(raw);

        const { homeCorner } = value.ui;
        if (homeCorner !== undefined && homeCorner !== null && homeCorner !== 'bottomLeft' && homeCorner !== 'bottomRight') {
            fail('ui.homeCorner', 'expected "bottomLeft", "bottomRight" or null');
        }
    }

    return value as GameConfig;
}

function allObjects(config: GameConfig): GameObject[] {
    return config.objects ?? [];
}

export function objectById(config: GameConfig, id: string): GameObject | null {
    return allObjects(config).find((object) => object.id === id) ?? null;
}

export function buildableObjects(config: GameConfig): GameObject[] {
    return allObjects(config).filter((object) => object.toBuild ?? !object.onMap);
}

function levelConfig(object: GameObject | null, level = 1): ObjectLevel | null {
    const levels = object?.levels ?? [];
    if (!levels.length) return null;
    const index = Math.min(Math.max(level, 1), levels.length) - 1;
    return levels[index]!;
}

export function maxLevel(object: GameObject | null): number {
    return (object?.levels ?? []).length || 1;
}

export function constructionCost(object: GameObject | null, level = 1): ResourceAmount[] {
    return levelConfig(object, level)?.construction ?? [];
}

export function productionLines(object: GameObject | null, level = 1): Production[] {
    const authored = levelConfig(object, level)?.production;
    if (!authored) return [];
    return (Array.isArray(authored) ? authored : [authored]).filter(Boolean);
}

export function production(object: GameObject | null, level = 1): Production | null {
    return productionLines(object, level)[0] ?? null;
}

export function questConditions(quest: Quest): QuestCondition[] {
    if (quest.conditions?.length) return quest.conditions;
    if (quest.goal === 'building' && quest.object) {
        return [{ type: 'building', object: quest.object, quantity: 1 }];
    }
    return [];
}

export function questRewards(quest: Quest): QuestReward[] {
    if (quest.rewards?.length) return quest.rewards;
    if (quest.reward?.resource) {
        return [{
            type: 'resource',
            resource: quest.reward.resource,
            quantity: quest.reward.quantity ?? 0,
        }];
    }
    return [];
}

export function hasFreeSlot(
    config: GameConfig,
    object: GameObject | null,
    standing: { objectId: string }[],
    slotOverrides: Record<string, number> = {},
): boolean {
    const category = (config.categories ?? []).find((c) => c.id === object?.category);
    if (!category) return true;

    const limit = slotOverrides[category.id] ?? category.slots;
    if (limit == null) return true;

    const inCategory = (config.objects ?? [])
        .filter((o) => o.category === category.id)
        .map((o) => o.id);

    const used = standing.filter(({ objectId }) => inCategory.includes(objectId)).length;
    return used < limit;
}

export function levelBuildTimeMs(object: GameObject | null, level = 1): number {
    return levelConfig(object, level)?.buildTimeMs ?? 0;
}

export function upgradeRule(object: GameObject | null, level = 1): Upgrade | null {
    return levelConfig(object, level)?.upgrade ?? null;
}

function resourceMap(config: GameConfig): Map<string, Resource> {
    return new Map((config.resources ?? []).map((r) => [r.id, r]));
}

export function resourceLabel(config: GameConfig, id: string): string {
    return resourceMap(config).get(id)?.label ?? id;
}

export function hudResources(config: GameConfig): Resource[] {
    const resources = config.resources ?? [];
    const starting = resources.filter((r) => r.starting);

    if (!starting.length) return resources;

    const currency = resources.find((r) => r.id === config.currency);
    if (currency && !starting.includes(currency)) return [currency, ...starting];

    return starting;
}

export function formatCost(config: GameConfig, cost: ResourceAmount[] | null): string {
    if (!cost?.length) return '';
    return cost
        .map(({ resource, quantity }) => `${quantity} ${resourceLabel(config, resource)}`)
        .join(', ');
}

export function sessionTimeLimitMs(config: GameConfig): number | null {
    const value = config.session?.timeLimitMs;
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}
